import { RiArrowRightLine, RiMapPinLine, RiTimeLine } from '@remixicon/react';
import CustomChip from '../../../components/CustomChip';
import { useLanguage } from '../../../contexts/language';
import { Language, TranslationRecord } from '../../../types/language';
import { JourneyStatusBadge } from '../../admin/Journeys/JourneyStatusBadges';
import { IslandJourneyDirection, PublicIslandJourney } from '../types';
import { getJourneyChanges } from './utils';
import { BADGE_CLASSNAMES } from '../../../utils/styles';
import { Card } from '@mui/material';

interface PublicJourneyCardProps {
  publicJourney: PublicIslandJourney;
  islandId: string;
  direction: IslandJourneyDirection;
}

const TimeDisplay = ({
  time,
  accentuated,
  lineThrough,
}: {
  time: string;
  lineThrough?: boolean;
  accentuated?: boolean;
}) => {
  return (
    <div
      className={[
        'flex items-center gap-1 text-sm whitespace-nowrap',
        lineThrough && 'line-through',
        accentuated && 'text-orange-600',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <RiTimeLine className={`text-xl`} />
      <span className={`text-base`}>
        {time.split(':').slice(0, 2).join(':')}
      </span>
    </div>
  );
};

const PortDisplay = ({
  portNames,
  islandNames,
  showIsland,
  language,
  lineThrough,
  accentuated,
}: {
  portNames: TranslationRecord;
  islandNames: TranslationRecord;
  showIsland: boolean;
  language: Language;
  lineThrough?: boolean;
  accentuated?: boolean;
}) => {
  const portName = portNames[language];
  const islandName = islandNames[language];
  return (
    <div
      className={[
        lineThrough && 'line-through',
        accentuated && 'text-orange-600',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {[showIsland && portName !== islandName && islandName, portName]
        .filter(Boolean)
        .join(' - ')}
    </div>
  );
};

const PublicJourneyCard = ({
  publicJourney,
  direction,
  islandId,
}: PublicJourneyCardProps) => {
  const { t, language } = useLanguage();
  const { timeMismatch, departureMismatch, arrivalMismatch } =
    getJourneyChanges(publicJourney, language);

  return (
    <Card className="p-4 flex flex-col gap-2">
      <div className="grid grid-cols-[min-content_1fr_min-content] gap-2 items-center">
        <div className="flex flex-col">
          {timeMismatch && publicJourney.routeDepartureTime && (
            <TimeDisplay time={publicJourney.routeDepartureTime} lineThrough />
          )}
          <TimeDisplay time={publicJourney.time} accentuated={timeMismatch} />
        </div>
        <div className="flex gap-2 overflow-hidden">
          <JourneyStatusBadge
            journeyStatus={publicJourney.journeyStatus}
            journeyDay={publicJourney.day}
            journeyTime={publicJourney.time}
            small
            showInPublicPage
          />
          <CustomChip
            label={t(`service-types.${publicJourney.serviceType}.name`)}
            className={`${BADGE_CLASSNAMES.BLUE} max-lg:hidden`}
            small
          />
        </div>
      </div>
      <CustomChip
        label={t(`service-types.${publicJourney.serviceType}.name`)}
        className={`${BADGE_CLASSNAMES.BLUE} w-fit lg:hidden`}
        small
      />
      <div className="inline-flex items-center gap-2 text-sm">
        <RiMapPinLine className="text-base" />
        <div>
          {departureMismatch &&
            publicJourney.routeDeparturePortNames &&
            publicJourney.routeDepartureIslandNames && (
              <PortDisplay
                portNames={publicJourney.routeDeparturePortNames}
                islandNames={publicJourney.routeDepartureIslandNames}
                language={language}
                showIsland={
                  direction === 'inbound' ||
                  publicJourney.routeDepartureIslandId !== islandId
                }
                lineThrough
              />
            )}
          <PortDisplay
            portNames={publicJourney.departurePortNames}
            islandNames={publicJourney.departureIslandNames}
            language={language}
            showIsland={direction === 'inbound'}
            accentuated={Boolean(departureMismatch)}
          />
        </div>
        <RiArrowRightLine className="text-base" />
        <div>
          {arrivalMismatch &&
            publicJourney.routeArrivalIslandNames &&
            publicJourney.routeArrivalPortNames && (
              <PortDisplay
                portNames={publicJourney.routeArrivalPortNames}
                islandNames={publicJourney.routeArrivalIslandNames}
                language={language}
                showIsland={
                  direction === 'outbound' ||
                  publicJourney.routeArrivalIslandId !== islandId
                }
                lineThrough
              />
            )}
          <PortDisplay
            portNames={publicJourney.arrivalPortNames}
            islandNames={publicJourney.arrivalIslandNames}
            language={language}
            showIsland={direction === 'outbound'}
            accentuated={Boolean(arrivalMismatch)}
          />
        </div>
      </div>

      <div className="text-base mt-1 flex justify-end">
        <span className="font-bold">{t('content.operated-by')}: &nbsp;</span>
        {publicJourney.operatorWeb ? (
          <a
            href={publicJourney.operatorWeb}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {publicJourney.operatorName}
          </a>
        ) : (
          <span>{publicJourney.operatorName}</span>
        )}
      </div>
    </Card>
  );
};

export default PublicJourneyCard;
